import * as React from "react";
import { useSearchParams } from "react-router";
import { useToggle } from "react-use";

import {
  useConfirmContractFamilyPipelineGroupingMutation,
  useGenerateContractFamilyPipelineSummariesMutation,
  useRerunContractFamilyPipelineSummaryColumnMutation,
  useGetContractFamilyPipelineRunQuery,
  useUpdateContractFamilyPipelinePromptsMutation,
  useRetryPipelineSnapshotsMutation,
  useExcludeFailedPipelineSnapshotsMutation,
  useStartPipelineCategorisationMutation,
  useEditContractFamilyPipelineGroupingMutation,
  snapshotCollectionRoute,
} from "@/api/contract-family-pipeline";
import { Button } from "@/components/ui/button";
import { useGetFamilySummaryFieldsQuery } from "@/api/contract-families";
import { PipelineGroupingValidation, PipelineStage, SummaryPromptsPayload } from "@/types/contract-family-pipeline";
import { CancelAutomatedRunButton } from "@/views/contract-families/components/CancelAutomatedRunButton";
import { buildContractFamiliesPath } from "@/views/contract-families/navigation";
import { contractFamilyStageOpened, contractFamilyStageRejected } from "@/services/analytics";

import { ExtractionSummaryStage } from "./ExtractionSummaryStage";
import { GroupingHierarchyStage } from "./GroupingHierarchyStage";
import { getPipelineErrorMessage, parsePipelineGroupingValidation } from "./pipelineErrors";
import { SnapshotCreationStage } from "./SnapshotCreationStage";
import { AddToMasterCollectionAction } from "./AddToMasterCollectionAction";
import { PipelineProgress } from "./PipelineProgress";
import { SummaryReviewStage } from "./SummaryReviewStage";
import { CompletedSummaryPromptEditor } from "./CompletedSummaryPromptEditor";
import { getAllowedDisplayStage, visiblePipelineStages } from "./stageNavigation";

interface Props {
  folderFseId?: string | null;
  groupingTxId?: string;
  blockingIssueCount: number;
  onWorkspaceChange?: (workspace: "families" | "review" | null, readOnly?: boolean) => void;
  onGroupingValidation?: () => void;
}

const stageLabels = {
  create_snapshots: "Create snapshots",
  categorise_review_confirm: "Categorise & confirm",
  summarise: "Create summaries",
};

/** What an automated run is doing at each stage, in the user's terms. */
const automaticStageLabels: Record<PipelineStage, string> = {
  setup: "Preparing the documents",
  snapshotting: "Creating snapshots",
  snapshot_review: "Creating snapshots",
  categorising: "Categorising contract families",
  review: "Confirming the contract families",
  confirmed: "Preparing the summaries",
  summarising: "Generating family summaries",
  summary_review: "Preparing publication",
  publishing: "Publishing to the Master Collection",
  complete: "Complete",
  failed: "Failed",
};

const POLL_INTERVAL_MS = 2000;
const AUTOMATIC_POLL_INTERVAL_MS = 10_000;

export const ContractFamilyPipeline = ({
  folderFseId,
  blockingIssueCount,
  onWorkspaceChange,
  onGroupingValidation,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const runId = searchParams.get("pipelineRunId");
  const [safeError, setSafeError] = React.useState<string | null>(null);
  const [groupingValidation, setGroupingValidation] = React.useState<PipelineGroupingValidation | null>(null);
  const [pollingInterval, setPollingInterval] = React.useState(0);
  const [confirmGrouping, confirmState] = useConfirmContractFamilyPipelineGroupingMutation();
  const [updatePrompts, promptState] = useUpdateContractFamilyPipelinePromptsMutation();
  const [generateSummaries, summaryState] = useGenerateContractFamilyPipelineSummariesMutation();
  const [rerunSummaryColumn, rerunState] = useRerunContractFamilyPipelineSummaryColumnMutation();
  const [editingCompletedPrompts, toggleEditingCompletedPrompts] = useToggle(false);
  const rerunIdempotencyKey = React.useRef<string | null>(null);
  const [retrySnapshots, retryState] = useRetryPipelineSnapshotsMutation();
  const [excludeSnapshots, excludeState] = useExcludeFailedPipelineSnapshotsMutation();
  const [startCategorisation, categorisationState] = useStartPipelineCategorisationMutation();
  const [editGrouping, editGroupingState] = useEditContractFamilyPipelineGroupingMutation();
  const runQuery = useGetContractFamilyPipelineRunQuery(runId ?? "", {
    skip: runId === null,
    pollingInterval,
  });
  const run = runQuery.currentData;
  // An automated run drives itself: the page only reports on it and offers to cancel it.
  const isRunningAutomatically = run?.automation === "auto_publish" && run.automation_state === "advancing";
  // A folder-mode run takes every folder as one family; there is no grouping to categorise or edit.
  const isFolderRun = run?.grouping_mode === "folder";

  const fieldsQuery = useGetFamilySummaryFieldsQuery(run?.original_collection_id ?? "", {
    skip: run?.original_collection_id === null || run?.original_collection_id === undefined,
  });
  const requestedStage = searchParams.get("stage");
  const displayStage = run ? getAllowedDisplayStage(run, requestedStage) : "categorise_review_confirm";
  const highestAvailableStage = run?.highest_available_stage;
  React.useEffect(() => {
    if (!highestAvailableStage) return;
    if (requestedStage && requestedStage !== displayStage) {
      contractFamilyStageRejected(requestedStage, highestAvailableStage);
    }
    contractFamilyStageOpened(displayStage);
  }, [displayStage, highestAvailableStage, requestedStage]);
  React.useEffect(() => {
    if (!run || requestedStage === displayStage) return;
    const next = new URLSearchParams(searchParams);
    next.set("stage", displayStage);
    setSearchParams(next, { replace: true });
    if (requestedStage) {
      setSafeError("That stage is not available yet. Showing the latest available stage.");
    }
  }, [displayStage, requestedStage, run, searchParams, setSearchParams]);
  React.useEffect(() => {
    if (run && displayStage === "categorise_review_confirm" && !["setup", "categorising"].includes(run.stage)) {
      onWorkspaceChange?.("families", run.stage !== "review");
      return;
    }
    onWorkspaceChange?.(null);
  }, [displayStage, onWorkspaceChange, run]);
  const isMutating =
    confirmState.isLoading ||
    promptState.isLoading ||
    summaryState.isLoading ||
    retryState.isLoading ||
    excludeState.isLoading ||
    categorisationState.isLoading ||
    editGroupingState.isLoading;
  const isPipelineMutating = isMutating || rerunState.isLoading;
  const summaryIsLive = Boolean(
    run?.summary_progress && (run.summary_progress.pending > 0 || run.summary_progress.processing > 0),
  );

  React.useEffect(() => {
    if (run?.error_code) setSafeError(getPipelineErrorMessage(run.error_code));
  }, [run?.error_code]);

  React.useEffect(() => {
    const isWorking = run?.stage === "categorising" || run?.stage === "summarising" || !!run?.snapshot_counts?.pending;
    setPollingInterval(isWorking ? POLL_INTERVAL_MS : isRunningAutomatically ? AUTOMATIC_POLL_INTERVAL_MS : 0);
  }, [isRunningAutomatically, run?.snapshot_counts?.pending, run?.stage]);

  React.useEffect(() => {
    setGroupingValidation(null);
    setSafeError(null);
  }, [runId]);

  const perform = React.useCallback(async (action: () => Promise<unknown>) => {
    setSafeError(null);
    try {
      await action();
    } catch (error) {
      setSafeError(getPipelineErrorMessage(error));
    }
  }, []);

  const onGenerate = React.useCallback(
    async (payload: SummaryPromptsPayload) => {
      if (!runId) return;
      await perform(async () => {
        await updatePrompts({ runId, payload }).unwrap();
        await generateSummaries(runId).unwrap();
        const next = new URLSearchParams(searchParams);
        next.set("stage", "summarise");
        setSearchParams(next);
        await runQuery.refetch();
      });
    },
    [generateSummaries, perform, runId, runQuery, searchParams, setSearchParams, updatePrompts],
  );

  const onRerunSummaryColumn = React.useCallback(
    async (fieldId: string, prompt: string) => {
      if (!run) return;
      rerunIdempotencyKey.current ??= crypto.randomUUID();
      await perform(async () => {
        const result = await rerunSummaryColumn({
          runId: run.id,
          payload: {
            extraction_field_id: fieldId,
            prompt: prompt.trim(),
            idempotency_key: rerunIdempotencyKey.current!,
          },
        }).unwrap();
        const next = new URLSearchParams(searchParams);
        next.set("pipelineRunId", result.pipeline_run_id);
        next.set("stage", "summarise");
        rerunIdempotencyKey.current = null;
        toggleEditingCompletedPrompts();
        setSearchParams(next);
      });
    },
    [perform, rerunSummaryColumn, run, searchParams, setSearchParams, toggleEditingCompletedPrompts],
  );

  const onConfirmGrouping = React.useCallback(async () => {
    if (!run) return;
    setSafeError(null);
    try {
      await confirmGrouping(run.id).unwrap();
      setGroupingValidation(null);
      await runQuery.refetch();
    } catch (error) {
      const validation = parsePipelineGroupingValidation(error);
      if (validation) {
        setGroupingValidation(validation);
        if (onGroupingValidation) {
          onGroupingValidation();
        } else {
          onWorkspaceChange?.("families");
        }
        return;
      }
      setSafeError(getPipelineErrorMessage(error));
    }
  }, [confirmGrouping, onGroupingValidation, onWorkspaceChange, run, runQuery]);

  const handleEditGrouping = React.useCallback(() => {
    if (!run) return;
    if (
      !window.confirm(
        "Edit grouping? Current staged summary review values will be discarded. The audit history will be retained.",
      )
    )
      return;
    void perform(async () => {
      await editGrouping(run.id).unwrap();
      await runQuery.refetch();
    });
  }, [editGrouping, perform, run, runQuery]);

  const initialPrompts = React.useMemo(
    () =>
      Object.fromEntries(
        (fieldsQuery.data?.fields ?? []).map(field => [
          field.extraction_field_id,
          run?.prompts_by_field_id[field.extraction_field_id] ?? "",
        ]),
      ),
    [fieldsQuery.data?.fields, run?.prompts_by_field_id],
  );

  return (
    <section
      className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-slate-950 md:px-7"
      aria-label="Family pipeline"
    >
      <ol className="grid grid-cols-1 gap-2 md:grid-cols-3">
        {visiblePipelineStages.map((stage, index) => {
          const isCurrent = stage === displayStage;
          const available = run
            ? visiblePipelineStages.indexOf(stage) <= visiblePipelineStages.indexOf(run.highest_available_stage)
            : false;

          return (
            <li key={stage}>
              <button
                type="button"
                disabled={!available}
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.set("stage", stage);
                  setSearchParams(next);
                }}
                aria-current={isCurrent ? "step" : undefined}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm ${
                  isCurrent
                    ? "border-desert-sand-400 bg-desert-sand-100 font-semibold text-slate-950"
                    : "border-slate-200 bg-white font-medium text-slate-500"
                }`}
              >
                <span className="mr-2 font-mono text-xs">{String(index + 1).padStart(2, "0")}</span>
                {stageLabels[stage]}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4 text-slate-900 shadow-sm">
        {run &&
        !isRunningAutomatically &&
        !isFolderRun &&
        ["confirmed", "summarising", "summary_review", "failed"].includes(run.stage) ? (
          <div className="mb-4 flex justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={summaryIsLive || editGroupingState.isLoading || rerunState.isLoading}
              onClick={handleEditGrouping}
            >
              Edit grouping
            </Button>
          </div>
        ) : null}
        {!runId ? (
          <p className="text-sm text-slate-600">Select files or folders in My Folders to create contract families.</p>
        ) : runQuery.isError && !run ? (
          <div role="alert" className="text-sm text-slate-700">
            <p>This workflow is not available to your account or no longer exists.</p>
            <a className="mt-2 inline-block font-medium underline" href={buildContractFamiliesPath(folderFseId)}>
              Back to Contract Families
            </a>
          </div>
        ) : runQuery.isLoading || !run ? (
          <p className="text-sm text-slate-500">Loading workflow…</p>
        ) : isRunningAutomatically ? (
          <div role="status" aria-live="polite" className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-desert-sand-200 border-t-desert-sand-700 motion-reduce:animate-none"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-950">This contract summary is running automatically</p>
              <p className="mt-1 text-sm text-slate-600">
                Current step: {automaticStageLabels[run.stage]}. It publishes to the Master Collection when it is done;
                this page updates on its own.
              </p>
              {run.stage === "summarising" && run.summary_progress ? (
                <PipelineProgress
                  progress={run.summary_progress}
                  label="Generating family summaries"
                  attemptId={run.summary_attempt_id}
                />
              ) : null}
              <CancelAutomatedRunButton runId={run.id} className="mt-3" />
            </div>
          </div>
        ) : displayStage === "create_snapshots" && !["setup", "snapshotting", "snapshot_review"].includes(run.stage) ? (
          <div>
            <p className="text-sm font-semibold text-slate-950">Source snapshots</p>
            <p className="mt-1 text-sm text-slate-600">This completed stage is read-only.</p>
          </div>
        ) : displayStage === "categorise_review_confirm" && !["setup", "categorising", "review"].includes(run.stage) ? (
          <div>
            <p className="mb-3 text-sm text-slate-600">This completed grouping revision is read-only.</p>
            <GroupingHierarchyStage mode="readonly" />
          </div>
        ) : run.stage === "setup" && isFolderRun ? (
          <p className="text-sm text-slate-600">
            This contract summary takes every folder as one contract family, so there is nothing to categorise.
          </p>
        ) : run.stage === "setup" ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Start categorisation, then review and confirm the proposed families.
            </p>
            <Button
              type="button"
              disabled={isMutating}
              onClick={() =>
                void perform(async () => {
                  await startCategorisation(run.id).unwrap();
                  await runQuery.refetch();
                })
              }
            >
              Start categorisation
            </Button>
          </div>
        ) : ["snapshotting", "snapshot_review"].includes(run.stage) ? (
          <SnapshotCreationStage
            run={run}
            isLoading={isPipelineMutating}
            onRetry={() =>
              void perform(async () => {
                await retrySnapshots(run.id).unwrap();
                await runQuery.refetch();
              })
            }
            onExclude={documentIds =>
              void perform(async () => {
                await excludeSnapshots({ runId: run.id, payload: { document_ids: documentIds } }).unwrap();
                await runQuery.refetch();
              })
            }
          />
        ) : run.stage === "categorising" ? (
          <div role="status" aria-live="polite" className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-desert-sand-200 border-t-desert-sand-700 motion-reduce:animate-none"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-950">Categorising contract families…</p>
              <p className="mt-1 text-sm text-slate-600">
                This may take a few minutes. Keep this page open; it updates automatically.
              </p>
              {run.categorisation_progress ? (
                <PipelineProgress
                  progress={run.categorisation_progress}
                  label="Categorising contract families"
                  attemptId={run.grouping_tx_id}
                />
              ) : (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full w-2/3 animate-pulse rounded-full bg-desert-sand-400 motion-reduce:animate-none" />
                </div>
              )}
            </div>
          </div>
        ) : run.stage === "review" ? (
          <GroupingHierarchyStage
            mode="editable"
            blockingIssueCount={blockingIssueCount}
            isLoading={isMutating}
            onConfirm={() => void onConfirmGrouping()}
          />
        ) : run.stage === "confirmed" ? (
          <ExtractionSummaryStage
            fields={fieldsQuery.data?.fields ?? []}
            initialPrompts={initialPrompts}
            extractionStarted
            snapshotFirst
            isLoading={isMutating}
            onStartExtraction={async () => undefined}
            onGenerate={onGenerate}
          />
        ) : run.stage === "summarising" ? (
          <div role="status" aria-live="polite" className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="mt-0.5 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-desert-sand-200 border-t-desert-sand-700 motion-reduce:animate-none"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-950">Generating family summaries…</p>
              <p className="mt-1 text-sm text-slate-600">
                This may take a few minutes. Keep this page open; it updates automatically.
              </p>
              {run.summary_progress ? (
                <PipelineProgress
                  progress={run.summary_progress}
                  label="Generating family summaries"
                  attemptId={run.summary_attempt_id}
                />
              ) : null}
            </div>
          </div>
        ) : run.stage === "summary_review" ? (
          <div className="space-y-4">
            {editingCompletedPrompts ? (
              <CompletedSummaryPromptEditor
                fields={fieldsQuery.data?.fields ?? []}
                prompts={initialPrompts}
                isLoading={rerunState.isLoading}
                onCancel={() => {
                  rerunIdempotencyKey.current = null;
                  toggleEditingCompletedPrompts();
                }}
                onRerun={onRerunSummaryColumn}
              />
            ) : (
              <>
                <SummaryReviewStage runId={run.id} />
                <div className="flex items-center gap-2">
                  {run.can_rerun_summary_column ? (
                    <Button type="button" variant="outline" onClick={toggleEditingCompletedPrompts}>
                      Edit prompts
                    </Button>
                  ) : null}
                  {run.can_publish ? (
                    <AddToMasterCollectionAction
                      runId={run.id}
                      targetUrl={
                        run.target_family_collection_id
                          ? snapshotCollectionRoute(run.template_id, run.target_family_collection_id)
                          : null
                      }
                    />
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : run.stage === "failed" && run.failed_stage === "summarising" ? (
          <div className="space-y-4">
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
              <p className="font-semibold">Summary generation failed</p>
              <p className="mt-1">{getPipelineErrorMessage(run.error_code)}</p>
            </div>
            <SummaryReviewStage runId={run.id} readOnly />
            <Button
              type="button"
              disabled={summaryState.isLoading}
              onClick={() =>
                void perform(async () => {
                  await generateSummaries(run.id).unwrap();
                  await runQuery.refetch();
                })
              }
            >
              Retry summaries
            </Button>
          </div>
        ) : run.stage === "complete" && displayStage === "summarise" ? (
          <div className="space-y-3">
            {editingCompletedPrompts ? (
              <CompletedSummaryPromptEditor
                fields={fieldsQuery.data?.fields ?? []}
                prompts={initialPrompts}
                isLoading={rerunState.isLoading}
                onCancel={() => {
                  rerunIdempotencyKey.current = null;
                  toggleEditingCompletedPrompts();
                }}
                onRerun={onRerunSummaryColumn}
              />
            ) : (
              <>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-slate-600">This completed summary review is read-only.</p>
                  {run.can_rerun_summary_column ? (
                    <Button type="button" variant="outline" onClick={toggleEditingCompletedPrompts}>
                      Edit prompts
                    </Button>
                  ) : null}
                </div>
                <SummaryReviewStage runId={run.id} readOnly />
              </>
            )}
          </div>
        ) : run.stage === "failed" ? (
          <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <p className="font-semibold">Workflow failed</p>
            <p className="mt-1">{getPipelineErrorMessage(run.error_code)}</p>
          </div>
        ) : (
          <div role="status" className="space-y-2">
            <p className="font-semibold">Family Collection updated</p>
            {run.target_family_collection_id ? (
              <a className="underline" href={snapshotCollectionRoute(run.template_id, run.target_family_collection_id)}>
                Open Family Collection
              </a>
            ) : null}
          </div>
        )}
        {run?.source_snapshot_collection_id &&
        displayStage === "create_snapshots" &&
        run.snapshot_counts?.pending === 0 ? (
          <a
            className="mt-4 inline-block text-sm font-medium underline"
            href={snapshotCollectionRoute(run.template_id, run.source_snapshot_collection_id)}
          >
            View snapshot collection
          </a>
        ) : null}
        {groupingValidation ? (
          <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            <p className="font-semibold">Grouping needs attention</p>
            <p className="mt-1">
              Each affected family must contain exactly one MASTER document. Update the document roles below, then
              confirm the grouping again.
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {groupingValidation.families.map(family => (
                <li key={family.family_id}>
                  <span className="font-medium">{family.name}</span>:{" "}
                  {family.master_count === 0 ? "No MASTER document" : `${family.master_count} MASTER documents`}
                </li>
              ))}
            </ul>
            {groupingValidation.total_count > groupingValidation.families.length ? (
              <p className="mt-2">
                And {groupingValidation.total_count - groupingValidation.families.length} more affected families.
              </p>
            ) : null}
          </div>
        ) : null}
        {safeError && run?.stage !== "failed" ? (
          <p className="mt-3 text-sm font-medium text-red-700">{safeError}</p>
        ) : null}
      </div>
    </section>
  );
};
